// src/screens/JobsScreen.js
// ============================================================================
// JOBS SCREEN - Public-facing careers page
//
// Pulls job postings from Firestore collection 'job_postings'.
// No auth required. Accessible from Footer and About screen.
//
// Firestore document structure (job_postings/{postingId}):
// {
//   title: string,              // "Founding Community Lead - Racing"
//   subtitle: string,           // "Equity-Based | Remote | Part-Time"
//   category: string,           // "Racing", "Fighting", "Operations", etc.
//   type: string,               // "equity", "paid", "hybrid"
//   status: string,             // "open", "closed", "paused"
//   summary: string,            // Brief 2-3 sentence description
//   mission: string,            // The Mission section
//   responsibilities: string[], // What You'll Do bullets
//   timeline: [                 // The 90-Day Sprint
//     { label: string, description: string }
//   ],
//   metrics: [                  // Success Metrics
//     { metric: string, target: string }
//   ],
//   whoYouAre: [                // Who You Are entries
//     { label: string, description: string }
//   ],
//   mustHave: string[],         // Must Have bullets
//   niceToHave: string[],       // Nice to Have bullets
//   niceToHaveNote: string,     // Working professionals note
//   compensation: [             // Compensation entries
//     { label: string, description: string }
//   ],
//   contactEmail: string,       // "Dennis.Kreger@CritCash.com"
//   createdAt: timestamp,
//   updatedAt: timestamp,
//   sortOrder: number,          // For manual ordering
// }
// ============================================================================

import React, { useState } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, ScrollView,
  TouchableOpacity, ActivityIndicator, Linking, Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';

// ============================================================================
// MAILTO HELPER - Prevents page refresh on mobile web (Chrome)
// ============================================================================
const openMailto = (email, subject) => {
  if (Platform.OS === 'web') {
    // On web, create a temporary anchor tag to handle mailto
    // This prevents the page from refreshing on mobile Chrome
    const anchor = document.createElement('a');
    anchor.href = `mailto:${email}?subject=${encodeURIComponent(subject)}`;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  } else {
    Linking.openURL(`mailto:${email}?subject=${encodeURIComponent(subject)}`);
  }
};

// ============================================================================
// TYPE BADGE COLORS
// ============================================================================
const TYPE_CONFIG = {
  equity: { label: 'Equity-Based', color: '#FCD000', bg: '#332b00' },
  paid: { label: 'Paid', color: '#22c55e', bg: '#0a2e14' },
  hybrid: { label: 'Hybrid', color: '#3b82f6', bg: '#0c1a3d' },
};

const CATEGORY_COLORS = {
  Racing: '#e74c3c',
  Fighting: '#9b59b6',
  Sports: '#2ecc71',
  Operations: '#3498db',
  Engineering: '#e67e22',
  Default: '#FCD000',
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export default function JobsScreen({ navigation }) {
  const [postings] = useState([]);
  const [loading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  // ============================================
  // TOGGLE EXPAND
  // ============================================
  const toggleExpand = (id) => {
    setExpandedId(prev => prev === id ? null : id);
  };

  // ============================================
  // RENDER POSTING CARD (COLLAPSED)
  // ============================================
  const renderCollapsedCard = (job) => {
    const typeConfig = TYPE_CONFIG[job.type] || TYPE_CONFIG.equity;
    const catColor = CATEGORY_COLORS[job.category] || CATEGORY_COLORS.Default;

    return (
      <TouchableOpacity
        key={job.id}
        style={styles.card}
        onPress={() => toggleExpand(job.id)}
        activeOpacity={0.7}
      >
        {/* Header Row */}
        <View style={styles.cardHeader}>
          <View style={styles.cardBadges}>
            <View style={[styles.badge, { backgroundColor: catColor + '22', borderColor: catColor }]}>
              <Text style={[styles.badgeText, { color: catColor }]}>{job.category}</Text>
            </View>
            <View style={[styles.badge, { backgroundColor: typeConfig.bg, borderColor: typeConfig.color }]}>
              <Text style={[styles.badgeText, { color: typeConfig.color }]}>{typeConfig.label}</Text>
            </View>
          </View>
          <Ionicons
            name={expandedId === job.id ? 'chevron-up' : 'chevron-down'}
            size={20}
            color="#666"
          />
        </View>

        {/* Title */}
        <Text style={styles.cardTitle}>{job.title}</Text>
        <Text style={styles.cardSubtitle}>{job.subtitle}</Text>

        {/* Summary */}
        <Text style={styles.cardSummary}>{job.summary}</Text>
      </TouchableOpacity>
    );
  };

  // ============================================
  // RENDER POSTING CARD (EXPANDED)
  // ============================================
  const renderExpandedCard = (job) => {
    const typeConfig = TYPE_CONFIG[job.type] || TYPE_CONFIG.equity;
    const catColor = CATEGORY_COLORS[job.category] || CATEGORY_COLORS.Default;

    return (
      <View key={job.id} style={[styles.card, styles.cardExpanded]}>
        {/* Collapse Header */}
        <TouchableOpacity
          style={styles.cardHeader}
          onPress={() => toggleExpand(job.id)}
          activeOpacity={0.7}
        >
          <View style={styles.cardBadges}>
            <View style={[styles.badge, { backgroundColor: catColor + '22', borderColor: catColor }]}>
              <Text style={[styles.badgeText, { color: catColor }]}>{job.category}</Text>
            </View>
            <View style={[styles.badge, { backgroundColor: typeConfig.bg, borderColor: typeConfig.color }]}>
              <Text style={[styles.badgeText, { color: typeConfig.color }]}>{typeConfig.label}</Text>
            </View>
          </View>
          <Ionicons name="chevron-up" size={20} color="#666" />
        </TouchableOpacity>

        {/* Title */}
        <Text style={styles.cardTitle}>{job.title}</Text>
        <Text style={styles.cardSubtitle}>{job.subtitle}</Text>

        {/* Divider */}
        <View style={styles.divider} />

        {/* Mission */}
        {job.mission ? (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>The Mission</Text>
            <Text style={styles.sectionBody}>{job.mission}</Text>
          </View>
        ) : null}

        {/* Responsibilities */}
        {job.responsibilities && job.responsibilities.length > 0 ? (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>What You'll Do</Text>
            {job.responsibilities.map((item, i) => (
              <View key={i} style={styles.bulletRow}>
                <Ionicons name="caret-forward" size={12} color="#FCD000" style={styles.bulletIcon} />
                <Text style={styles.bulletText}>{item}</Text>
              </View>
            ))}
          </View>
        ) : null}

        {/* 90-Day Sprint */}
        {job.timeline && job.timeline.length > 0 ? (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>The 90-Day Sprint</Text>
            {job.timeline.map((item, i) => (
              <View key={i} style={styles.timelineRow}>
                <Text style={styles.timelineLabel}>{item.label}</Text>
                <Text style={styles.timelineDesc}>{item.description}</Text>
              </View>
            ))}
          </View>
        ) : null}

        {/* Metrics */}
        {job.metrics && job.metrics.length > 0 ? (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Success Metrics</Text>
            <View style={styles.metricsContainer}>
              {job.metrics.map((item, i) => (
                <View key={i} style={styles.metricRow}>
                  <Text style={styles.metricLabel}>{item.metric}</Text>
                  <Text style={styles.metricTarget}>{item.target}</Text>
                </View>
              ))}
            </View>
          </View>
        ) : null}

        {/* Who You Are */}
        {job.whoYouAre && job.whoYouAre.length > 0 ? (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Who You Are</Text>
            {job.whoYouAre.map((item, i) => (
              <Text key={i} style={styles.whoYouAreItem}>
                <Text style={styles.whoYouAreLabel}>{item.label} </Text>
                <Text style={styles.whoYouAreDesc}>{item.description}</Text>
              </Text>
            ))}
          </View>
        ) : null}

        {/* Must Have */}
        {job.mustHave && job.mustHave.length > 0 ? (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Must Have</Text>
            {job.mustHave.map((item, i) => (
              <View key={i} style={styles.bulletRow}>
                <Ionicons name="checkmark-circle" size={14} color="#22c55e" style={styles.bulletIcon} />
                <Text style={styles.bulletText}>{item}</Text>
              </View>
            ))}
          </View>
        ) : null}

        {/* Nice to Have */}
        {job.niceToHave && job.niceToHave.length > 0 ? (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Nice to Have</Text>
            {job.niceToHave.map((item, i) => (
              <View key={i} style={styles.bulletRow}>
                <Ionicons name="add-circle-outline" size={14} color="#666" style={styles.bulletIcon} />
                <Text style={styles.bulletText}>{item}</Text>
              </View>
            ))}
            {job.niceToHaveNote ? (
              <Text style={styles.niceToHaveNote}>{job.niceToHaveNote}</Text>
            ) : null}
          </View>
        ) : null}

        {/* Compensation */}
        {job.compensation && job.compensation.length > 0 ? (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>The Upside</Text>
            {job.compensation.map((item, i) => (
              <Text key={i} style={styles.compItem}>
                <Text style={styles.compLabel}>{item.label}: </Text>
                <Text style={styles.compDesc}>{item.description}</Text>
              </Text>
            ))}
          </View>
        ) : null}

        {/* Apply CTA */}
        {job.contactEmail ? (
          <TouchableOpacity
            style={styles.applyButton}
            onPress={() => openMailto(job.contactEmail, `Application: ${job.title}`)}
          >
            <Ionicons name="mail" size={18} color="#000" style={{ marginRight: 8 }} />
            <Text style={styles.applyButtonText}>Apply Now</Text>
          </TouchableOpacity>
        ) : null}

        <Text style={styles.applyEmail}>
          Or email directly: {job.contactEmail}
        </Text>

        {/* Social Links */}
        <View style={styles.socialRow}>
          <TouchableOpacity
            style={styles.socialLink}
            onPress={() => Linking.openURL('https://x.com/CritCash')}
          >
            <Ionicons name="logo-twitter" size={16} color="#999" />
            <Text style={styles.socialText}>@CritCash</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.socialLink}
            onPress={() => Linking.openURL('https://www.linkedin.com/company/critcash-inc')}
          >
            <Ionicons name="logo-linkedin" size={16} color="#999" />
            <Text style={styles.socialText}>CritCash Inc.</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  // ============================================
  // RENDER
  // ============================================
  return (
    <SafeAreaView style={styles.container}>
      <Header navigation={navigation} />
      <ScrollView contentContainerStyle={styles.scrollContent}>
        {/* Header */}
        <Text style={styles.headline}>Careers at CritCash</Text>
        <Text style={styles.tagline}>
          Help us grow the streaming platform for gamers.
        </Text>

        {/* Intro */}
        <View style={styles.introCard}>
          <Ionicons name="people" size={24} color="#FCD000" />
          <Text style={styles.introText}>
            CritCash is the streaming platform for gamers, with the tools built in for
            streamers to grow and engage their audience: saved videos, squad streams, and
            their own events and leagues. We're
            looking for people who live and breathe it to help us grow.
          </Text>
        </View>

        {/* Postings */}
        {loading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#FCD000" />
            <Text style={styles.loadingText}>Loading positions...</Text>
          </View>
        ) : postings.length === 0 ? (
          <View style={styles.emptyContainer}>
            <Ionicons name="briefcase-outline" size={48} color="#333" />
            <Text style={styles.emptyTitle}>No Open Positions</Text>
            <Text style={styles.emptyText}>
              We don't have any open roles posted right now, but we're always open to reviewing
              applications from passionate gamers. Reach out at jobs@critcash.com.
            </Text>
          </View>
        ) : (
          <View style={styles.postingsContainer}>
            {postings.map(job =>
              expandedId === job.id
                ? renderExpandedCard(job)
                : renderCollapsedCard(job)
            )}
          </View>
        )}

        {/* General inquiries */}
        {postings.length > 0 ? (
          <View style={styles.generalInquiry}>
            <Text style={styles.generalText}>
              Don't see a role that fits? We're always looking for passionate people.
            </Text>
            <TouchableOpacity
              onPress={() => openMailto('jobs@critcash.com', 'General Inquiry')}
            >
              <Text style={styles.generalLink}>jobs@critcash.com</Text>
            </TouchableOpacity>
          </View>
        ) : null}

        {/* Back Button */}
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Lobby'))}
        >
          <Text style={styles.backButtonText}>Back</Text>
        </TouchableOpacity>

      </ScrollView>
      <Footer navigation={navigation} />
      <NavBar navigation={navigation} />
    </SafeAreaView>
  );
}

// ============================================================================
// STYLES
// ============================================================================
const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#000',
  },
  scrollContent: {
    flexGrow: 1,
    alignItems: 'center',
    padding: 16,
    paddingBottom: 32,
  },

  // Header
  headline: {
    color: '#FCD000',
    fontSize: 28,
    fontWeight: 'bold',
    textAlign: 'center',
    marginTop: 16,
    marginBottom: 8,
  },
  tagline: {
    color: '#999',
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 24,
    fontStyle: 'italic',
  },

  // Intro
  introCard: {
    width: '100%',
    maxWidth: 600,
    backgroundColor: '#111',
    borderRadius: 12,
    padding: 16,
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 24,
    borderWidth: 1,
    borderColor: '#222',
  },
  introText: {
    color: '#AAA',
    fontSize: 14,
    lineHeight: 22,
    flex: 1,
    marginLeft: 12,
  },

  // Postings
  postingsContainer: {
    width: '100%',
    maxWidth: 600,
  },

  // Card (Collapsed)
  card: {
    backgroundColor: '#111',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#222',
  },
  cardExpanded: {
    borderColor: '#FCD000',
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  cardBadges: {
    flexDirection: 'row',
    gap: 8,
  },
  badge: {
    paddingHorizontal: 10,
    paddingVertical: 3,
    borderRadius: 12,
    borderWidth: 1,
  },
  badgeText: {
    fontSize: 11,
    fontWeight: '700',
  },
  cardTitle: {
    color: '#FFF',
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  cardSubtitle: {
    color: '#666',
    fontSize: 13,
    marginBottom: 10,
  },
  cardSummary: {
    color: '#AAA',
    fontSize: 14,
    lineHeight: 20,
  },

  // Divider
  divider: {
    height: 1,
    backgroundColor: '#222',
    marginVertical: 16,
  },

  // Sections
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    color: '#FCD000',
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  sectionBody: {
    color: '#CCC',
    fontSize: 14,
    lineHeight: 22,
  },

  // Bullets
  bulletRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 6,
  },
  bulletIcon: {
    marginRight: 8,
    marginTop: 2,
  },
  bulletText: {
    color: '#CCC',
    fontSize: 14,
    lineHeight: 20,
    flex: 1,
  },

  // Timeline
  timelineRow: {
    marginBottom: 10,
  },
  timelineLabel: {
    color: '#FCD000',
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 2,
  },
  timelineDesc: {
    color: '#AAA',
    fontSize: 14,
    lineHeight: 20,
  },

  // Metrics
  metricsContainer: {
    backgroundColor: '#0a0a0a',
    borderRadius: 8,
    overflow: 'hidden',
  },
  metricRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#1a1a1a',
  },
  metricLabel: {
    color: '#FFF',
    fontSize: 14,
    fontWeight: '600',
    flex: 1,
  },
  metricTarget: {
    color: '#999',
    fontSize: 14,
    flex: 1,
    textAlign: 'right',
  },

  // Who You Are
  whoYouAreItem: {
    marginBottom: 8,
    lineHeight: 20,
  },
  whoYouAreLabel: {
    color: '#FCD000',
    fontSize: 14,
    fontWeight: 'bold',
  },
  whoYouAreDesc: {
    color: '#CCC',
    fontSize: 14,
  },

  // Nice to Have Note
  niceToHaveNote: {
    color: '#888',
    fontSize: 13,
    lineHeight: 20,
    marginTop: 10,
    fontStyle: 'italic',
  },

  // Compensation
  compItem: {
    marginBottom: 8,
    lineHeight: 20,
  },
  compLabel: {
    color: '#FCD000',
    fontSize: 14,
    fontWeight: 'bold',
  },
  compDesc: {
    color: '#CCC',
    fontSize: 14,
  },

  // Apply CTA
  applyButton: {
    backgroundColor: '#FCD000',
    borderRadius: 8,
    paddingVertical: 14,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 16,
    marginBottom: 8,
  },
  applyButtonText: {
    color: '#000',
    fontSize: 16,
    fontWeight: 'bold',
  },
  applyEmail: {
    color: '#666',
    fontSize: 12,
    textAlign: 'center',
  },

  // Social Links
  socialRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 12,
    gap: 20,
  },
  socialLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  socialText: {
    color: '#999',
    fontSize: 13,
  },

  // Loading
  loadingContainer: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  loadingText: {
    color: '#888',
    marginTop: 12,
    fontSize: 14,
  },

  // Empty State
  emptyContainer: {
    alignItems: 'center',
    paddingVertical: 40,
    width: '100%',
    maxWidth: 600,
  },
  emptyTitle: {
    color: '#FFF',
    fontSize: 18,
    fontWeight: 'bold',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyText: {
    color: '#888',
    fontSize: 14,
    lineHeight: 22,
    textAlign: 'center',
  },

  // General Inquiry
  generalInquiry: {
    alignItems: 'center',
    marginTop: 16,
    marginBottom: 24,
  },
  generalText: {
    color: '#888',
    fontSize: 14,
    marginBottom: 4,
  },
  generalLink: {
    color: '#FCD000',
    fontSize: 14,
    textDecorationLine: 'underline',
  },

  // Back Button
  backButton: {
    paddingVertical: 12,
    paddingHorizontal: 32,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#333',
    marginBottom: 24,
  },
  backButtonText: {
    color: '#FFF',
    fontSize: 14,
    fontWeight: '600',
  },
});